import { NavLink, Outlet } from "react-router-dom";
import {
  CalendarDays,
  LayoutDashboard,
  MessageCircle,
  PenSquare,
  Plug,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useUnlinkedEngagementCount } from "./api";
import { SOCIAL_ENGAGEMENT_ENABLED } from "@/lib/social-flags";

/* ------------------------------------------------------------------ */
/*  Tab definition                                                     */
/* ------------------------------------------------------------------ */

interface HubTab {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** `end` so the index tab (Overview) only matches the exact path. */
  end?: boolean;
}

const TABS: HubTab[] = [
  { to: "/social", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/social/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/social/engagements", label: "Engagements", icon: MessageCircle },
  { to: "/social/accounts", label: "Accounts", icon: Plug },
];

/* ------------------------------------------------------------------ */
/*  SocialHubLayout                                                    */
/* ------------------------------------------------------------------ */

/**
 * Shared chrome for the Social section. Renders the app shell, a sticky tab
 * bar that surfaces every social surface (Overview / Calendar / Engagements /
 * Accounts) plus a primary Compose action, then the active tab via <Outlet>.
 *
 * Children (the nested routes) render their own page container + heading and
 * must NOT wrap themselves in <AppShell> — this layout owns the chrome.
 */
export function SocialHubLayout() {
  // Engagements tab shows a presence dot when there are unlinked engagements
  // (only meaningful once the engagement pipeline flag is live).
  const { data: unlinkedCount = 0 } = useUnlinkedEngagementCount();
  const showEngagementDot = SOCIAL_ENGAGEMENT_ENABLED && unlinkedCount > 0;

  return (
    <AppShell>
      <div className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6">
          <nav className="-mb-px flex items-center gap-1 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end}
                  className={({ isActive }) =>
                    cn(
                      "group relative flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors",
                      isActive
                        ? "border-primary text-foreground"
                        : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  <span className="relative">
                    {tab.label}
                    {tab.to === "/social/engagements" && showEngagementDot ? (
                      <span className="absolute -right-2.5 -top-0.5 h-2 w-2 rounded-full bg-destructive" />
                    ) : null}
                  </span>
                </NavLink>
              );
            })}
          </nav>
          <Button asChild size="sm" className="shrink-0">
            <NavLink to="/social/compose">
              <PenSquare className="mr-2 h-4 w-4" />
              Compose
            </NavLink>
          </Button>
        </div>
      </div>

      <Outlet />
    </AppShell>
  );
}

export default SocialHubLayout;
